import React, { useState } from 'react';

// ── STORAGE HELPERS ───────────────────────────────────────────────────────────

function loadGlobal() {
  try { return JSON.parse(localStorage.getItem('omni_global') || '{}'); }
  catch { return {}; }
}

function saveGlobal(data) {
  localStorage.setItem('omni_global', JSON.stringify(data));
}

function loadCompanyConfig(companyId) {
  try { return JSON.parse(localStorage.getItem(`omni_company_${companyId}`) || '{}'); }
  catch { return {}; }
}

function saveCompanyConfig(companyId, data) {
  localStorage.setItem(`omni_company_${companyId}`, JSON.stringify(data));
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────

export default function SettingsPanel({ companies, instances, onUpdateCompanies, onUpdateInstances, companyId }) {
  const [tab, setTab] = useState('instances');
  const [newComp, setNewComp] = useState({ name: '', color: '#6366f1' });
  const [newInst, setNewInst] = useState({ name: '', company: companyId || '', channel: 'whatsapp' });

  // Config global
  const [globalConfig, setGlobalConfig] = useState(() => ({
    EVOLUTION_KEY: '',
    N8N_BASE: 'http://localhost:5678',
    N8N_API_KEY: '',
    ...loadGlobal(),
  }));
  const [globalSaved, setGlobalSaved] = useState(false);

  // Config por empresa (carrega a empresa ativa)
  const [companyConfig, setCompanyConfig] = useState(() => ({
    WORKFLOW_DISPARADOR_TEXTO: '',
    WORKFLOW_DISPARADOR_MIDIA: '',
    WEBHOOK_SDR_AGENT: '',
    REDIS_TOGGLE_WEBHOOK: 'https://webhook.perpe.pro/webhook/toggle-agent',
    ...loadCompanyConfig(companyId),
  }));
  const [companySaved, setCompanySaved] = useState(false);

  const companyInstances = instances.filter(i => !companyId || i.company === companyId);
  const activeCompany = companies.find(c => c.id === companyId);

  const s = {
    container: { flex: 1, background: '#141417', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    header: { padding: '16px 20px', borderBottom: '1px solid #27272a' },
    title: { fontSize: 16, fontWeight: 600, color: '#e4e4e7' },
    sub: { fontSize: 12, color: '#71717a', marginTop: 2 },
    tabs: { display: 'flex', gap: 4, padding: '10px 20px', borderBottom: '1px solid #27272a' },
    tab: (a) => ({
      padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500,
      border: `1px solid ${a ? '#6366f1' : '#3f3f46'}`,
      background: a ? '#6366f1' : 'transparent',
      color: a ? '#fff' : '#71717a', cursor: 'pointer',
    }),
    content: { flex: 1, overflowY: 'auto', padding: 20 },
    sTitle: { fontSize: 11, fontWeight: 600, color: '#52525b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
    card: {
      background: '#1c1c1f', border: '1px solid #27272a', borderRadius: 10,
      padding: 14, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12,
    },
    cardInfo: { flex: 1 },
    cardName: { fontSize: 13, fontWeight: 500, color: '#e4e4e7' },
    cardSub: { fontSize: 11, color: '#71717a', marginTop: 2 },
    badge: (a) => ({
      padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600,
      background: a ? '#1e3a2f' : '#27272a', color: a ? '#22c55e' : '#52525b',
    }),
    addForm: {
      background: '#1c1c1f', border: '1px solid #3f3f46', borderRadius: 10,
      padding: 14, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap',
    },
    input: {
      flex: 1, minWidth: 140, padding: '7px 10px',
      background: '#18181b', border: '1px solid #3f3f46',
      borderRadius: 6, color: '#e4e4e7', fontSize: 12, outline: 'none',
    },
    select: {
      padding: '7px 10px', background: '#18181b', border: '1px solid #3f3f46',
      borderRadius: 6, color: '#e4e4e7', fontSize: 12, outline: 'none',
    },
    addBtn: {
      padding: '7px 14px', background: '#6366f1', border: 'none',
      borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer',
    },
    delBtn: {
      padding: '4px 8px', background: 'transparent', border: '1px solid #3f3f46',
      borderRadius: 5, color: '#ef4444', fontSize: 11, cursor: 'pointer',
    },
    divider: { borderBottom: '1px solid #27272a', margin: '18px 0' },
    formGroup: { marginBottom: 14 },
    label: {
      fontSize: 11, fontWeight: 600, color: '#71717a', marginBottom: 5,
      display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em',
    },
    formInput: {
      width: '100%', padding: '8px 12px',
      background: '#18181b', border: '1px solid #3f3f46',
      borderRadius: 8, color: '#e4e4e7', fontSize: 13, outline: 'none',
    },
    saveRow: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 20 },
    saveBtn: (color) => ({
      padding: '9px 20px', background: color || '#6366f1', border: 'none',
      borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer',
    }),
    savedMsg: { fontSize: 12, color: '#22c55e' },
    sectionBox: (color) => ({
      background: '#1c1c1f',
      border: `1px solid ${color || '#27272a'}`,
      borderRadius: 10,
      padding: 16,
      marginBottom: 20,
    }),
    sectionHeader: (color) => ({
      fontSize: 13, fontWeight: 600, color: color || '#e4e4e7',
      marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
    }),
    note: (color) => ({
      background: color + '15', border: `1px solid ${color}44`,
      borderRadius: 8, padding: 10, fontSize: 11, color: color,
      marginBottom: 16, lineHeight: 1.6,
    }),
  };

  const addCompany = () => {
    if (!newComp.name.trim()) return;
    const id = newComp.name.toLowerCase().replace(/\s+/g, '_');
    onUpdateCompanies([...companies, { id, ...newComp }]);
    setNewComp({ name: '', color: '#6366f1' });
  };

  const addInstance = () => {
    if (!newInst.name.trim() || !newInst.company) return;
    const id = newInst.name.toLowerCase().replace(/\W+/g, '_');
    onUpdateInstances([...instances, { id, ...newInst, active: true }]);
    setNewInst({ name: '', company: companyId || '', channel: 'whatsapp' });
  };

  const handleSaveGlobal = () => {
    saveGlobal(globalConfig);
    setGlobalSaved(true);
    setTimeout(() => setGlobalSaved(false), 2000);
  };

  const handleSaveCompany = () => {
    saveCompanyConfig(companyId, companyConfig);
    setCompanySaved(true);
    setTimeout(() => setCompanySaved(false), 2000);
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div style={s.title}>⚙️ Configurações</div>
        <div style={s.sub}>Workspace: {activeCompany?.name || '—'}</div>
      </div>

      <div style={s.tabs}>
        {[
          { id: 'instances', label: '📱 Instâncias' },
          { id: 'companies', label: '🏢 Empresas' },
          { id: 'company_config', label: '🎯 Fluxos da empresa' },
          { id: 'global', label: '🌐 Global' },
        ].map(t => (
          <button key={t.id} style={s.tab(tab === t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      <div style={s.content}>

        {/* ── INSTÂNCIAS ──────────────────────────────────────────────── */}
        {tab === 'instances' && (
          <>
            <div style={s.sTitle}>Instâncias — {activeCompany?.name}</div>
            {companyInstances.length === 0 && (
              <div style={{ color: '#52525b', fontSize: 13, marginBottom: 12 }}>Nenhuma instância cadastrada.</div>
            )}
            {companyInstances.map(i => {
              const company = companies.find(c => c.id === i.company);
              return (
                <div key={i.id} style={s.card}>
                  <span style={{ fontSize: 20 }}>📱</span>
                  <div style={s.cardInfo}>
                    <div style={s.cardName}>{i.name}</div>
                    <div style={s.cardSub}>{company?.name} · {i.channel}</div>
                  </div>
                  <span style={s.badge(i.active)}>{i.active ? '● Ativo' : '○ Inativo'}</span>
                  <button style={s.delBtn} onClick={() => onUpdateInstances(instances.filter(x => x.id !== i.id))}>
                    Remover
                  </button>
                </div>
              );
            })}
            <div style={s.divider} />
            <div style={s.sTitle}>Adicionar instância</div>
            <div style={s.addForm}>
              <input style={s.input} placeholder="Nome (ex: SOFIA_Perpe.AI)"
                value={newInst.name} onChange={e => setNewInst(d => ({ ...d, name: e.target.value }))} />
              <select style={s.select} value={newInst.company}
                onChange={e => setNewInst(d => ({ ...d, company: e.target.value }))}>
                <option value="">Empresa...</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select style={s.select} value={newInst.channel}
                onChange={e => setNewInst(d => ({ ...d, channel: e.target.value }))}>
                <option value="whatsapp">WhatsApp</option>
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
              </select>
              <button style={s.addBtn} onClick={addInstance}>+ Adicionar</button>
            </div>
          </>
        )}

        {/* ── EMPRESAS ────────────────────────────────────────────────── */}
        {tab === 'companies' && (
          <>
            <div style={s.sTitle}>Empresas cadastradas</div>
            {companies.map(c => (
              <div key={c.id} style={s.card}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.color }} />
                <div style={s.cardInfo}>
                  <div style={s.cardName}>{c.name}</div>
                  <div style={s.cardSub}>{instances.filter(i => i.company === c.id).length} instância(s)</div>
                </div>
                <button style={s.delBtn} onClick={() => onUpdateCompanies(companies.filter(x => x.id !== c.id))}>
                  Remover
                </button>
              </div>
            ))}
            <div style={s.divider} />
            <div style={s.sTitle}>Adicionar empresa</div>
            <div style={s.addForm}>
              <input style={s.input} placeholder="Nome da empresa"
                value={newComp.name} onChange={e => setNewComp(d => ({ ...d, name: e.target.value }))} />
              <input type="color" value={newComp.color}
                onChange={e => setNewComp(d => ({ ...d, color: e.target.value }))}
                style={{ width: 36, height: 32, border: 'none', background: 'none', cursor: 'pointer' }} />
              <button style={s.addBtn} onClick={addCompany}>+ Adicionar</button>
            </div>
          </>
        )}

        {/* ── CONFIG POR EMPRESA ──────────────────────────────────────── */}
        {tab === 'company_config' && (
          <>
            <div style={s.note(activeCompany?.color || '#6366f1')}>
              🎯 Configurações exclusivas de <strong>{activeCompany?.name}</strong>. Cada empresa tem seus próprios fluxos n8n e agente.
            </div>

            <div style={s.sectionBox(activeCompany?.color + '44')}>
              <div style={s.sectionHeader(activeCompany?.color)}>
                🚀 Disparadores n8n
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>ID Workflow Disparador Texto</label>
                <input style={s.formInput} placeholder="xxxxxxxx-xxxx-xxxx"
                  value={companyConfig.WORKFLOW_DISPARADOR_TEXTO}
                  onChange={e => setCompanyConfig(c => ({ ...c, WORKFLOW_DISPARADOR_TEXTO: e.target.value }))} />
                <div style={{ fontSize: 11, color: '#52525b', marginTop: 4 }}>
                  n8n → abre o workflow → ID na URL
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>ID Workflow Disparador Mídia</label>
                <input style={s.formInput} placeholder="xxxxxxxx-xxxx-xxxx"
                  value={companyConfig.WORKFLOW_DISPARADOR_MIDIA}
                  onChange={e => setCompanyConfig(c => ({ ...c, WORKFLOW_DISPARADOR_MIDIA: e.target.value }))} />
              </div>
            </div>

            <div style={s.sectionBox()}>
              <div style={s.sectionHeader()}>
                🤖 Agente SDR
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Webhook SDR Agent (Production URL)</label>
                <input style={s.formInput} placeholder="https://webhook.perpe.pro/webhook/xxxxxxxx"
                  value={companyConfig.WEBHOOK_SDR_AGENT}
                  onChange={e => setCompanyConfig(c => ({ ...c, WEBHOOK_SDR_AGENT: e.target.value }))} />
                <div style={{ fontSize: 11, color: '#52525b', marginTop: 4 }}>
                  n8n → fluxo SDR → node Webhook → Production URL
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Webhook Toggle Agente (Redis)</label>
                <input style={s.formInput} placeholder="https://webhook.perpe.pro/webhook/toggle-agent"
                  value={companyConfig.REDIS_TOGGLE_WEBHOOK}
                  onChange={e => setCompanyConfig(c => ({ ...c, REDIS_TOGGLE_WEBHOOK: e.target.value }))} />
              </div>
            </div>

            <div style={s.saveRow}>
              <button style={s.saveBtn(activeCompany?.color)} onClick={handleSaveCompany}>
                💾 Salvar configurações de {activeCompany?.name}
              </button>
              {companySaved && <span style={s.savedMsg}>✓ Salvo!</span>}
            </div>
          </>
        )}

        {/* ── GLOBAL ──────────────────────────────────────────────────── */}
        {tab === 'global' && (
          <>
            <div style={s.note('#71717a')}>
              🌐 Configurações compartilhadas por todas as empresas. Evolution API, n8n e Cloudinary são os mesmos para todos os workspaces.
            </div>

            <div style={s.sectionBox()}>
              <div style={s.sectionHeader()}>⚡ Evolution API</div>
              <div style={s.formGroup}>
                <label style={s.label}>API Key</label>
                <input style={s.formInput} type="password" placeholder="sua_api_key"
                  value={globalConfig.EVOLUTION_KEY}
                  onChange={e => setGlobalConfig(c => ({ ...c, EVOLUTION_KEY: e.target.value }))} />
              </div>
            </div>

            <div style={s.sectionBox()}>
              <div style={s.sectionHeader()}>🔧 n8n</div>
              <div style={s.formGroup}>
                <label style={s.label}>URL base</label>
                <input style={s.formInput} placeholder="http://localhost:5678"
                  value={globalConfig.N8N_BASE}
                  onChange={e => setGlobalConfig(c => ({ ...c, N8N_BASE: e.target.value }))} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>API Key</label>
                <input style={s.formInput} type="password" placeholder="sua_n8n_api_key"
                  value={globalConfig.N8N_API_KEY}
                  onChange={e => setGlobalConfig(c => ({ ...c, N8N_API_KEY: e.target.value }))} />
              </div>
            </div>

            <div style={s.saveRow}>
              <button style={s.saveBtn()} onClick={handleSaveGlobal}>💾 Salvar configurações globais</button>
              {globalSaved && <span style={s.savedMsg}>✓ Salvo!</span>}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
